import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api } from "./api";
import type { User } from "./types";

type Tab = "profile" | "security" | "monday";

export function Settings({ user, onClose, onUserChange, onImported }: {
  user: User; onClose: () => void; onUserChange: (u: User) => void; onImported: (boardId: string) => void;
}) {
  const [tab, setTab] = useState<Tab>("profile");
  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="tabs" style={{ marginBottom: 16, padding: 0 }}>
          <button className={"tab" + (tab === "profile" ? " active" : "")} onClick={() => setTab("profile")}>Profil</button>
          <button className={"tab" + (tab === "security" ? " active" : "")} onClick={() => setTab("security")}>Sicherheit & 2FA</button>
          <button className={"tab" + (tab === "monday" ? " active" : "")} onClick={() => setTab("monday")}>Monday Import</button>
        </div>
        {tab === "profile" ? <ProfileTab user={user} onUserChange={onUserChange} />
          : tab === "security" ? <SecurityTab user={user} onUserChange={onUserChange} />
          : <MondayTab onImported={onImported} />}
        <div style={{ marginTop: 18, textAlign: "right" }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Schließen</button>
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ user, onUserChange }: { user: User; onUserChange: (u: User) => void }) {
  const [name, setName] = useState(user.name);
  const [nameMsg, setNameMsg] = useState("");

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwMsg, setPwMsg] = useState("");

  const roleLabel: Record<string, string> = { admin: "Admin", producer: "Producer", member: "Mitarbeiter" };

  async function saveName() {
    setNameMsg("");
    await api.updateProfile(name);
    onUserChange({ ...user, name });
    setNameMsg("Gespeichert.");
  }

  async function changePassword() {
    setPwErr(""); setPwMsg("");
    if (newPw.length < 8) { setPwErr("Neues Passwort: mindestens 8 Zeichen."); return; }
    if (newPw !== confirmPw) { setPwErr("Passwörter stimmen nicht überein."); return; }
    try {
      await api.changePassword(curPw, newPw);
      setCurPw(""); setNewPw(""); setConfirmPw("");
      setPwMsg("Passwort geändert.");
    } catch (e: any) {
      const code: string = e.body?.error ?? "";
      if (code === "invalid_credentials") setPwErr("Aktuelles Passwort ist falsch.");
      else if (code === "weak_password") setPwErr("Neues Passwort: mindestens 8 Zeichen.");
      else setPwErr("Passwort konnte nicht geändert werden.");
    }
  }

  return (
    <div>
      <h2>Profil</h2>

      <div className="field">
        <label>Anzeigename</label>
        <input className="input" value={name} onChange={(e) => { setName(e.target.value); setNameMsg(""); }} />
      </div>
      <div className="row-flex" style={{ alignItems: "center", gap: 8, marginBottom: 4 }}>
        <button className="btn btn-sm" onClick={saveName}>Speichern</button>
        {nameMsg && <span className="muted" style={{ fontSize: 13 }}>{nameMsg}</span>}
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label>E-Mail</label>
        <div className="input muted" style={{ background: "var(--bg-2)", cursor: "default" }}>{user.email}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Die E-Mail-Adresse kann nicht geändert werden.</div>
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label>Rolle</label>
        <div><span className="tag">{roleLabel[user.role] ?? user.role}</span></div>
      </div>

      <h3 style={{ marginTop: 24, marginBottom: 12 }}>Passwort ändern</h3>
      <div className="field">
        <label>Aktuelles Passwort</label>
        <input className="input" type="password" value={curPw} onChange={(e) => { setCurPw(e.target.value); setPwErr(""); setPwMsg(""); }} autoComplete="current-password" />
      </div>
      <div className="field">
        <label>Neues Passwort</label>
        <input className="input" type="password" value={newPw} onChange={(e) => { setNewPw(e.target.value); setPwErr(""); setPwMsg(""); }} autoComplete="new-password" />
      </div>
      <div className="field">
        <label>Neues Passwort bestätigen</label>
        <input className="input" type="password" value={confirmPw} onChange={(e) => { setConfirmPw(e.target.value); setPwErr(""); setPwMsg(""); }} autoComplete="new-password" />
      </div>
      <div className="err">{pwErr}</div>
      <div className="row-flex" style={{ alignItems: "center", gap: 8 }}>
        <button className="btn btn-sm" onClick={changePassword}>Passwort ändern</button>
        {pwMsg && <span className="muted" style={{ fontSize: 13 }}>{pwMsg}</span>}
      </div>
    </div>
  );
}

function SecurityTab({ user, onUserChange }: { user: User; onUserChange: (u: User) => void }) {
  const [step, setStep] = useState<"idle" | "setup" | "recovery">("idle");
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[]>([]);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");

  async function begin() {
    setErr("");
    const r = await api.setup2fa();
    setSecret(r.secret);
    setQr(await QRCode.toDataURL(r.uri, { margin: 1, width: 200 }));
    setStep("setup");
  }
  async function confirm() {
    setErr("");
    try {
      const r = await api.enable2fa(code);
      setRecovery(r.recoveryCodes);
      setStep("recovery");
      onUserChange({ ...user, totpEnabled: true });
    } catch { setErr("Code ungültig."); }
  }
  async function disable() {
    setErr("");
    try {
      await api.disable2fa(pw);
      onUserChange({ ...user, totpEnabled: false });
      setStep("idle"); setPw("");
    } catch { setErr("Passwort falsch."); }
  }

  if (user.totpEnabled && step === "idle")
    return (
      <div>
        <h2>Zwei-Faktor-Authentifizierung</h2>
        <p className="muted">2FA ist <strong style={{ color: "var(--green)" }}>aktiv</strong> für {user.email}.</p>
        <div className="field"><label>Passwort zum Deaktivieren</label>
          <input className="input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
        <div className="err">{err}</div>
        <button className="btn btn-danger btn-sm" onClick={disable}>2FA deaktivieren</button>
      </div>
    );

  if (step === "recovery")
    return (
      <div>
        <h2>Recovery-Codes</h2>
        <p className="muted">Bewahre diese sicher auf. Jeder Code funktioniert einmal, falls du keinen Zugriff auf deine App hast.</p>
        <div className="recovery-grid">{recovery.map((c) => <div key={c}>{c}</div>)}</div>
        <button className="btn btn-sm" onClick={() => navigator.clipboard?.writeText(recovery.join("\n"))}>Kopieren</button>
      </div>
    );

  if (step === "setup")
    return (
      <div>
        <h2>2FA einrichten</h2>
        <p className="muted">Scanne den QR-Code mit Google Authenticator, Authy oder 1Password.</p>
        <div className="qr">{qr && <img src={qr} width={180} height={180} alt="QR" />}</div>
        <p className="muted">Oder manuell eingeben:</p>
        <div className="secret-box">{secret}</div>
        <div className="field" style={{ marginTop: 14 }}><label>6-stelliger Code zur Bestätigung</label>
          <input className="input code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" /></div>
        <div className="err">{err}</div>
        <button className="btn" onClick={confirm}>Aktivieren</button>
      </div>
    );

  return (
    <div>
      <h2>Zwei-Faktor-Authentifizierung</h2>
      <p className="muted">Schütze dein Konto mit einem zeitbasierten Code (TOTP) zusätzlich zum Passwort.</p>
      <button className="btn" onClick={begin}>2FA aktivieren</button>
    </div>
  );
}

function MondayTab({ onImported }: { onImported: (boardId: string) => void }) {
  const [connected, setConnected] = useState<boolean | null>(null);
  const [token, setToken] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [boards, setBoards] = useState<Array<{ id: string; name: string; items_count: number }>>([]);
  const [msg, setMsg] = useState("");

  useEffect(() => { api.mondayStatus().then((r) => setConnected(r.connected)).catch(() => setConnected(false)); }, []);
  useEffect(() => { if (connected) loadBoards(); }, [connected]);

  async function loadBoards() {
    try { const r = await api.mondayBoards(); setBoards(r.boards); }
    catch (e: any) { setErr(e.body?.message || "Konnte Boards nicht laden."); }
  }
  async function connect() {
    setErr(""); setBusy(true);
    try { const r = await api.saveMondayToken(token); setConnected(true); setMsg(`Verbunden${r.account ? ` als ${r.account.name}` : ""}.`); }
    catch (e: any) { setErr(e.body?.message || "Token ungültig."); }
    finally { setBusy(false); }
  }
  async function importBoard(id: string, name: string) {
    setErr(""); setBusy(true); setMsg(`Importiere „${name}“…`);
    try { const r = await api.importMonday(id); setMsg(`„${r.name}“ importiert (${r.imported} Projekte).`); onImported(r.boardId); }
    catch (e: any) { setErr(e.body?.message || "Import fehlgeschlagen."); setMsg(""); }
    finally { setBusy(false); }
  }

  if (connected === null) return <div className="spin" />;

  return (
    <div>
      <h2>Monday.com Import</h2>
      {!connected ? (
        <>
          <p className="muted">Füge deinen Monday API-Token ein (Monday → Profil → Entwickler → API). Er wird verschlüsselt gespeichert.</p>
          <div className="field"><label>API-Token</label>
            <input className="input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="eyJhbGc…" /></div>
          <div className="err">{err}</div>
          <button className="btn" disabled={busy || token.length < 20} onClick={connect}>{busy ? "…" : "Verbinden"}</button>
        </>
      ) : (
        <>
          <p className="muted">{msg || "Verbunden. Wähle ein Board zum Importieren:"}</p>
          <div className="err">{err}</div>
          <div style={{ maxHeight: 280, overflow: "auto" }}>
            {boards.map((b) => (
              <div className="row-flex" key={b.id} style={{ justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <div><strong>{b.name}</strong><div className="muted">{b.items_count} Items</div></div>
                <button className="btn btn-sm" disabled={busy} onClick={() => importBoard(b.id, b.name)}>Importieren</button>
              </div>
            ))}
            {!boards.length && <p className="muted">Keine Boards gefunden.</p>}
          </div>
        </>
      )}
    </div>
  );
}
